import type { TimeOffEntry, TimeOffType } from '../../../api/timeOff';
import { isTimeOffEntryEnded } from './timeOffEntriesGridFilters';
import type { TimeOffEntrySyncState } from './timeOffEntryMutability';
import {
  toLocalAllDayDate,
  toLocalAllDaySchedulerEndDate
} from '../../../utils/timezone';
import { getTimeOffEntrySyncState } from './timeOffEntryMutability';

export interface TimeOffSchedulerEvent {
  id: string;
  text: string;
  employeeUpn: string;
  employeeDisplayName: string | null;
  startDate: Date;
  endDate: Date;
  allDay: boolean;
  type: TimeOffType;
  status: string;
  syncState: TimeOffEntrySyncState;
  notes: string | null;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  rowVersion: number;
  calendarTargetIds: string[];
  calendarTargetNames: string[];
  needsEmployeeIdentity: boolean;
  ended: boolean;
}

export function formatSchedulerEventLabel(entry: TimeOffEntry): string {
  const name = entry.needsEmployeeIdentity
    ? `⚠ Needs employee · ${entry.employeeDisplayName ?? entry.employeeUpn}`
    : (entry.employeeDisplayName ?? entry.employeeUpn);
  return `${entry.type} · ${name}`;
}

export function toSchedulerEvent(entry: TimeOffEntry): TimeOffSchedulerEvent {
  return {
    id: entry.id,
    text: formatSchedulerEventLabel(entry),
    employeeUpn: entry.employeeUpn,
    employeeDisplayName: entry.employeeDisplayName,
    startDate: entry.isAllDay ? toLocalAllDayDate(entry.startUtc) : new Date(entry.startUtc),
    endDate: entry.isAllDay ? toLocalAllDaySchedulerEndDate(entry.endUtc) : new Date(entry.endUtc),
    allDay: entry.isAllDay,
    type: entry.type,
    status: entry.status,
    syncState: getTimeOffEntrySyncState(entry),
    notes: entry.notes,
    startUtc: entry.startUtc,
    endUtc: entry.endUtc,
    isAllDay: entry.isAllDay,
    rowVersion: entry.rowVersion,
    calendarTargetIds: entry.calendarLinks.map((l) => l.calendarTargetId),
    calendarTargetNames: entry.calendarLinks.map((l) => l.calendarTargetDisplayName),
    needsEmployeeIdentity: entry.needsEmployeeIdentity,
    ended: isTimeOffEntryEnded(entry)
  };
}

export function syncStateAriaLabel(syncState: TimeOffEntrySyncState): string | undefined {
  switch (syncState) {
    case 'Waiting':
      return 'Waiting to sync with Outlook';
    case 'Error':
      return 'Sync error';
    case 'No targets':
      return 'No calendar targets';
    default:
      return undefined;
  }
}

export function appointmentStatusClassName(status: string): string {
  if (status === 'Pending') return 'time-off-appt--pending';
  if (status === 'Rejected') return 'time-off-appt--rejected';
  if (status === 'Cancelled') return 'time-off-appt--cancelled';
  return 'time-off-appt--approved';
}

export function appointmentLifecycleClassName(status: string, ended: boolean): string {
  const statusClass = appointmentStatusClassName(status);
  if (status === 'Cancelled' || !ended) return statusClass;
  return `${statusClass} time-off-appt--past`;
}

export function appointmentSyncClassName(syncState: TimeOffEntrySyncState): string | null {
  switch (syncState) {
    case 'Waiting':
      return 'time-off-appt__sync--waiting';
    case 'Error':
      return 'time-off-appt__sync--error';
    case 'No targets':
      return 'time-off-appt__sync--no-targets';
    default:
      return null;
  }
}
